import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import {
  INTL_DMY_CASUAL_FORMAT,
  INTL_SHORT_TIME_FORMAT_24_HOUR,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import { take } from 'lib/array/arrayUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useMemo, useState } from 'react'
import Image from 'components/Common/Image/Image'
import styled from 'styled-components'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import TextLink from 'LuxKit/TextLink'
import Expander from 'components/utils/Expander'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import switchFunc from 'lib/function/switchFunc'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineUtensilsAltIcon from 'LuxKit/Icons/line/LineUtensilsAltIcon'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineReturnFlightAltIcon from 'LuxKit/Icons/line/LineReturnFlightAltIcon'
import { isSpoofed } from 'selectors/isSpoofed'
import { connect } from 'react-redux'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import { useIntl } from 'react-intl'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'
import LineCancelIcon from 'LuxKit/Icons/line/LineCancelIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import type { FlightCabinCountLabel, FlightCabinFareRuleLabel } from 'lib/flights/flightCabinCountLabels'
import { AIRLINE_CODE_JETSTAR } from 'constants/flight'

const featureToIconMap = switchFunc<React.ReactElement, App.FareFamilyFeatureType>({
  meals: <LineUtensilsAltIcon />,
  seat_reservation: <LineSeatIcon />,
  checked_baggage: <LineBagIcon />,
  changes: <LineReturnFlightAltIcon />,
  carry_on_bag: <LineBagIcon />,
  cancellation: <LineCancelIcon />,
  refunds: <LineRefundIcon />,
  insurance: <LineShieldCheckIcon />,
}, <LineCheckIcon />)

const Details = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
`

// Need the margin internal to the slide down
const FlightMoreDetails = styled(VerticalSpacer)`
  margin-top: ${rem(16)};
`

const FlightItemLocation = styled.div`
  padding: ${rem(12)} 0;
`

const FlightItem = styled.div`
  display: grid;
  grid-template-columns: ${rem(72)} 1px 1fr;
  padding: 0 ${rem(16)};
  gap: ${rem(16)};
`

const ItemLine = styled(Divider)`
  position: relative;
  align-self: stretch;
  height: auto;

  &::before {
    content: '';
    position: absolute;
    background-color: ${props => props.theme.palette.neutral.surface.normalForeground};
    top: 50%;
    inset-inline-start: 50%;
    transform: translateY(-50%) translateX(-50%);
    height: ${rem(8)};
    width: ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.round};
    border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground};

    [dir="rtl"] & {
      transform: translateY(-50%) translateX(50%);
    }
  }
`

const FlightInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(4)};
`

const DetailLine = styled(Divider)`
  position: relative;
  align-self: stretch;
  height: auto;
`

const DetailLineIcon = styled(LinePlaneDepartureIcon)`
  position: absolute;
  top: ${rem(22)};
  inset-inline-start: 50%;
  transform: translateY(-50%) translateX(-50%);
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

  [dir="rtl"] & {
    transform: translateY(-50%) translateX(50%);
  }
`

const FlightDetailItem = styled(FlightItem)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const CarrierLogo = styled(Image)`
  max-width: ${rem(72)};
  width: auto;
`

function parseFlightDateTime(dateTime: string) {
  const parsedDateTime = moment(dateTime, [
    moment.ISO_8601,
    `${ISO_DATE_FORMAT} HH:mm`,
    `${ISO_DATE_FORMAT} h:mm A`,
    `${ISO_DATE_FORMAT} h:mma`,
    `${'DD/MM/YYYY'} HH:mm`,
    `${'DD/MM/YYYY'} h:mm A`,
    `${'DD/MM/YYYY'} h:mma`,
  ], true)

  return parsedDateTime.isValid() ? parsedDateTime.toDate() : new Date(dateTime)
}

export interface HiddenStopOverride {
  departureAirport: string
  departureAirportName: string
  departingTime: string
  departingDate?: string
  arrivalAirport: string
  arrivalAirportName: string
  arrivalTime: string
  arrivalDate?: string
  duration: { hours: number; minutes: number }
}

export type SnapshotDetailsFlight = Pick<App.LegFlight,
  'departureAirport' | 'departureAirportName' | 'departingDate' | 'departingTime' |
  'arrivalAirport' | 'arrivalAirportName' | 'arrivalDate' | 'arrivalTime' |
  'flightNumber' | 'aircraftType' | 'fareClass' | 'bookingClass' | 'operatingAirline'
> & { flightTime?: App.HoursMinutes }

interface Props {
  journey: Pick<App.Leg, 'links'> & { fareFamily?: App.JourneyV2['fareFamily'] }
  flight: App.FlightV2 | App.LegFlight | SnapshotDetailsFlight
  flightIndex: number
  fareFamily?: App.FlightFareFamily
  inclusions?: App.InclusionFeatures
  addedBaggage?: App.InclusionFeatures
  defaultOpen?: boolean
  enableViewFareRulesButton: boolean
  onShowFareRules: (urls: string, params?: any) => void
  isSpoofed: boolean
  withInsurance?: boolean
  hiddenStopOverride?: HiddenStopOverride
  cabinCountLabels?: Array<FlightCabinCountLabel>
  cabinFareRuleLabels?: Array<FlightCabinFareRuleLabel>
  fareRulesLink?: React.ReactNode
  additionalDetails?: React.ReactNode
}

function generateIncludedInsuranceFeature(group: number, description: string): App.FareFamilyFeature {
  return { status: 'INCLUDED', code: 'insurance', group, category: 'extra', description, type: 'insurance' }
}

export function JourneyV2DetailsModalFlight(props: Props) {
  const {
    journey,
    flight,
    flightIndex,
    fareFamily,
    inclusions,
    addedBaggage,
    defaultOpen = false,
    enableViewFareRulesButton,
    onShowFareRules,
    withInsurance,
    hiddenStopOverride,
    cabinCountLabels,
    cabinFareRuleLabels,
    additionalDetails,
    fareRulesLink,
  } = props

  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const [showMore, setShowMore] = useState<boolean>(defaultOpen)

  const departureAirport = hiddenStopOverride?.departureAirport ?? flight.departureAirport
  const departureAirportName = hiddenStopOverride?.departureAirportName ?? flight.departureAirportName
  const departingTime = hiddenStopOverride?.departingTime ?? flight.departingTime
  const arrivalAirport = hiddenStopOverride?.arrivalAirport ?? flight.arrivalAirport
  const arrivalAirportName = hiddenStopOverride?.arrivalAirportName ?? flight.arrivalAirportName
  const arrivalTime = hiddenStopOverride?.arrivalTime ?? flight.arrivalTime

  const showLessLabel = intl.formatMessage({
    id: 'MSK819G-OvnHLekL0yaI',
    defaultMessage: 'Show less',
    description: 'Link to collapse flight details in the flight search or booking flow',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'H4jaksR6Lhp0klj6OVzF',
    defaultMessage: 'Show more',
    description: 'Link text to expand and show more details about the selected flight itinerary',
  })

  const cancellationProtectionLabel = intl.formatMessage(bookingProtectionGlossaryMessages.cancellationProtection)

  const seatSelectionLabel = intl.formatMessage({
    id: 'cIaZ3gNrhRG58L2XjqBg',
    defaultMessage: 'Seat selection',
    description: 'Feature label for seat selection availability on a flight',
  })

  const features = useMemo((): App.InclusionFeatures => {
    if (inclusions) {
      const insuranceFeatures: Array<App.FareFamilyFeature> = withInsurance ? [generateIncludedInsuranceFeature(inclusions.length, cancellationProtectionLabel)] : []
      return [...take(inclusions, 6), ...insuranceFeatures]
    }

    if (fareFamily) {
      const includedFeatures = fareFamily.fareFamily.fareFeatures.filter(feature => feature.status === 'INCLUDED')

      const insuranceFeatures: Array<App.FareFamilyFeature> = withInsurance ? [generateIncludedInsuranceFeature(includedFeatures.length, cancellationProtectionLabel)] : []

      if (addedBaggage) {
        if (addedBaggage?.length > 0 && flight.operatingAirline.code === AIRLINE_CODE_JETSTAR) {
          return [...take(includedFeatures, 6), ...insuranceFeatures, ...addedBaggage]
        }
      }
      return [...take(includedFeatures, 6), ...insuranceFeatures]
    }

    // not a fare family, so we have to build the features list ourselves
    const flightFeatures: App.InclusionFeatures = []
    if ('includedCarryOnBaggage' in flight && flight.includedCarryOnBaggage) {
      flightFeatures.push({
        description: flight.includedCarryOnBaggage.description,
        type: 'carry_on_bag',
      })
    }

    if ('includedCheckedBaggage' in flight && flight.includedCheckedBaggage) {
      flightFeatures.push({
        description: flight.includedCheckedBaggage.description,
        type: 'checked_baggage',
      })
    } else if (addedBaggage) {
      if (addedBaggage?.length > 0 && flight.operatingAirline.code === AIRLINE_CODE_JETSTAR) {
        flightFeatures.push(...addedBaggage)
      }
    }

    if ('seatsSelectionAvailable' in flight && flight.seatsSelectionAvailable) {
      flightFeatures.push({
        description: seatSelectionLabel,
        type: 'seat_reservation',
      })
    }

    if (withInsurance) {
      flightFeatures.push(generateIncludedInsuranceFeature(flightFeatures.length, cancellationProtectionLabel))
    }

    return flightFeatures
  }, [inclusions, fareFamily, flight, addedBaggage, withInsurance, cancellationProtectionLabel, seatSelectionLabel])

  const openFareRulesModal = useCallback(() => {
    const hasMoreThanOneLink = journey.links.length > 1
    const links = journey.links
    const firstLink = journey.links[0]?.value
    const linkByFlightIndex = links[flightIndex]?.value

    let fareRulesUrl = firstLink
    if (hasMoreThanOneLink) {
      fareRulesUrl = linkByFlightIndex
    }

    if (cabinFareRuleLabels?.[0]?.url) {
      fareRulesUrl = cabinFareRuleLabels[0].url
    }

    if (!fareRulesUrl) {
      return
    }

    onShowFareRules(fareRulesUrl)
  }, [journey.links, flightIndex, onShowFareRules, cabinFareRuleLabels])

  const fareFamilyDescription = useMemo(() => {
    return fareFamily?.fareFamily.description ?? journey.fareFamily?.description ?? flight.fareClass
  }, [fareFamily?.fareFamily.description, flight.fareClass, journey])

  const bookingClass = useMemo(() => {
    return fareFamily?.bookingClass ?? flight.bookingClass
  }, [fareFamily?.bookingClass, flight.bookingClass])

  const flightTime = hiddenStopOverride?.duration ?? ('duration' in flight ? flight.duration : flight.flightTime)

  const departureDateTime = useMemo(() => {
    const date = hiddenStopOverride ? hiddenStopOverride.departingDate : flight.departingDate
    const time = hiddenStopOverride ? hiddenStopOverride.departingTime : flight.departingTime
    return date ? `${date} ${time}` : undefined
  }, [hiddenStopOverride, flight.departingDate, flight.departingTime])

  const arrivalDateTime = useMemo(() => {
    const date = hiddenStopOverride ? hiddenStopOverride.arrivalDate : flight.arrivalDate
    const time = hiddenStopOverride ? hiddenStopOverride.arrivalTime : flight.arrivalTime
    return date ? `${date} ${time}` : undefined
  }, [hiddenStopOverride, flight.arrivalDate, flight.arrivalTime])

  const departureDateTimeDate = useMemo(
    () => departureDateTime ? parseFlightDateTime(departureDateTime) : undefined,
    [departureDateTime],
  )
  const arrivalDateTimeDate = useMemo(
    () => arrivalDateTime ? parseFlightDateTime(arrivalDateTime) : undefined,
    [arrivalDateTime],
  )

  const departureDateCaption = useMemo(() => {
    if (!departureDateTimeDate) return undefined
    return dateFormatter(departureDateTimeDate, INTL_DMY_CASUAL_FORMAT)
  }, [dateFormatter, departureDateTimeDate])

  const arrivalDateCaption = useMemo(() => {
    if (!arrivalDateTimeDate) return undefined
    return dateFormatter(arrivalDateTimeDate, INTL_DMY_CASUAL_FORMAT)
  }, [arrivalDateTimeDate, dateFormatter])

  const flightNumberLabel = intl.formatMessage(
    {
      id: 'ipiNR7NAGAXAMjedwrjY',
      defaultMessage: '{airlineCode}-{flightNumber}',
      description: 'Flight number with airline code in flight details modal',
    },
    { airlineCode: flight.operatingAirline.code, flightNumber: flight.flightNumber },
  )

  const fareClassDisplayText = bookingClass
    ? intl.formatMessage(
        {
          id: '6RNE49yGUQwoNQPHEwc9',
          defaultMessage: '{description} ({bookingClass})',
          description: 'Fare class description with booking class code in flight details modal',
        },
        { description: fareFamilyDescription, bookingClass },
      )
    : fareFamilyDescription
  const cabinCountDisplayTexts = useMemo(() => {
    if (!cabinCountLabels?.length) {
      return []
    }

    return cabinCountLabels.map(cabinCountLabel => intl.formatMessage(
      {
        id: '2HtqiKBzHNZXtXucXdOW',
        defaultMessage: '{count}x {cabin}',
        description: 'Cabin passenger count label on a My Escapes charter flight tile after a per-passenger cabin upgrade',
      },
      {
        count: cabinCountLabel.count,
        cabin: cabinCountLabel.label,
      },
    ))
  }, [cabinCountLabels, intl])
  const hasMixedCabinCounts = cabinCountDisplayTexts.length > 0

  return <Details>
    <FlightItem>
      <FlightItemLocation>
        <BodyText align="end" variant="small" weight="bold">
          {departureDateTimeDate ? dateFormatter(departureDateTimeDate, INTL_SHORT_TIME_FORMAT_24_HOUR) : departingTime}
        </BodyText>
        {departureDateTimeDate && <Caption align="end" variant="smallalt" tabletVariant="medium" colour="light-foreground-alt" wrap="no-wrap">
          {departureDateCaption}
        </Caption>}
      </FlightItemLocation>
      <ItemLine kind="primary" direction="vertical" />
      <FlightItemLocation>
        <BodyText variant="small" weight="bold">{departureAirport}</BodyText>
        <Caption variant="smallalt" tabletVariant="medium" colour="light-foreground-alt">
          {intl.formatMessage(
            {
              id: 'NbPpSpzhQUrbmOStnuFT',
              defaultMessage: '{airportName} ({airportCode})',
              description: 'Departure airport name with code in flight journey detail modal',
            },
            { airportName: departureAirportName, airportCode: departureAirport },
          )}
        </Caption>
      </FlightItemLocation>
    </FlightItem>
    <FlightDetailItem>
      <FlightItemLocation>
        <CarrierLogo
          width={72}
          image={{
            url: flight.operatingAirline.logoUrl,
            title: flight.operatingAirline.name,
          }}
        />
      </FlightItemLocation>
      <DetailLine kind="primary" direction="vertical">
        <DetailLineIcon size="XS" />
      </DetailLine>
      <FlightItemLocation>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LabelGroup>
            <StructureLabel variant="standard">{flight.operatingAirline.name}</StructureLabel>
          </LabelGroup>
          <TextLink
            size="caption"
            weight="regular"
            wrap="no-wrap"
            onClick={() => setShowMore(val => !val)}
          >
            {showMore ? showLessLabel : showMoreLabel}
          </TextLink>
        </Group>
        <Expander expanded={showMore}>
          <FlightMoreDetails gap={16}>
            <FlightInfoGrid>
              <Caption variant="large" weight="bold" style={{ gridColumn: 'span 2' }}>
                {intl.formatMessage({
                  id: 'a_IX-l7dy0ol7vZTqK83',
                  defaultMessage: 'Flight information',
                  description: 'Section title for flight information in details modal',
                })}
              </Caption>
              {!!flight.flightNumber && <>
                <Caption variant="large">
                  {intl.formatMessage({
                    id: 'TICuLZcfyZvZsqP5xFyQ',
                    defaultMessage: 'Flight number',
                    description: 'Label for flight number in details modal in the flights flow',
                  })}
                </Caption><Caption variant="large" align="end">{flightNumberLabel}</Caption>
              </>}
              {
                (!!flightTime?.hours || !!flightTime?.minutes) &&
                <>
                  <Caption variant="large">
                    {intl.formatMessage({
                      id: 'rk-S5pNV2ABmNAeHhbkv',
                      defaultMessage: 'Duration',
                      description: 'Label for flight duration in details modal in the flights flow',
                    })}
                  </Caption>
                  <Caption variant="large" align="end">
                    {intl.formatMessage({
                      id: 'P-F0imj_9xVFsORB6cyo',
                      defaultMessage: '{hoursLabel}, {minutesLabel}',
                      description: 'Full flight duration display showing hours and minutes',
                    }, {
                      hoursLabel: intl.formatMessage({
                        id: '51D2rdv7hOKY2gZCM-IT',
                        defaultMessage: '{hours, plural, one {# hour} other {# hours}}',
                        description: 'Hour count label used inside the flight duration display',
                      }, { hours: flightTime.hours }),
                      minutesLabel: intl.formatMessage({
                        id: 'yjdndvQ2SjsEV5LEJyju',
                        defaultMessage: '{minutes, plural, one {# minute} other {# minutes}}',
                        description: 'Minute count label used inside the flight duration display',
                      }, { minutes: flightTime.minutes }),
                    })}
                  </Caption>
                </>
              }
              {!!flight.aircraftType && <>
                <Caption variant="large">
                  {intl.formatMessage({
                    id: 'lgJWrWwCxDPo--AjWQiq',
                    defaultMessage: 'Aircraft',
                    description: 'Form field label for aircraft type in flight details modal',
                  })}
                </Caption><Caption variant="large" align="end">{flight.aircraftType}</Caption>
              </>}
            </FlightInfoGrid>
            {(fareClassDisplayText || hasMixedCabinCounts || enableViewFareRulesButton) && <VerticalSpacer gap={4}>
              <FlightInfoGrid>
                <Caption variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'xSt6OEsgghJRMop50Vrg',
                    defaultMessage: 'Fare class',
                    description: 'Label for fare class in flight details modal in the flights flow',
                  })}
                </Caption>
                {enableViewFareRulesButton && <Caption variant="large" align="end">
                  {fareRulesLink ?? <TextLink
                    size="caption"
                    weight="regular"
                    onClick={() => openFareRulesModal()}
                  >
                    {intl.formatMessage({
                      id: 'sGZSYnOda2ZHpCAcAz9T',
                      defaultMessage: 'View fare rules',
                      description: 'Link text to view fare rules in flight details modal',
                    })}
                  </TextLink>}
                </Caption>}
              </FlightInfoGrid>
              {cabinCountDisplayTexts.length > 0 && cabinCountDisplayTexts.map(cabinCountDisplayText => (
                <Caption key={cabinCountDisplayText} variant="large">{cabinCountDisplayText}</Caption>
              ))}
              {cabinCountDisplayTexts.length === 0 && !!fareClassDisplayText && <Caption variant="large">{fareClassDisplayText}</Caption>}
            </VerticalSpacer>}
            {features.length > 0 && !hasMixedCabinCounts && <VerticalSpacer gap={4}>
              <Caption variant="large" weight="bold">
                {intl.formatMessage({
                  id: '55fWHISWA_nu6zpJm3e2',
                  defaultMessage: 'Inclusions',
                  description: 'Section title for included features in flight details modal',
                })}
              </Caption>
              {features.map(feature =>
                <CaptionTextBlock
                  variant="medium"
                  colour="light-foreground-alt"
                  key={feature.description}
                  startIcon={featureToIconMap(feature.type)}
                >
                  {feature.description}
                </CaptionTextBlock>)}
            </VerticalSpacer>}
            {additionalDetails}
          </FlightMoreDetails>
        </Expander>
      </FlightItemLocation>
    </FlightDetailItem>
    <FlightItem>
      <FlightItemLocation>
        <BodyText align="end" variant="small" weight="bold">
          {arrivalDateTimeDate ? dateFormatter(arrivalDateTimeDate, INTL_SHORT_TIME_FORMAT_24_HOUR) : arrivalTime}
        </BodyText>
        {arrivalDateTimeDate && <Caption align="end" variant="smallalt" tabletVariant="medium" colour="light-foreground-alt" wrap="no-wrap">{arrivalDateCaption}</Caption>}
      </FlightItemLocation>
      <ItemLine kind="primary" direction="vertical" />
      <FlightItemLocation>
        <BodyText variant="small" weight="bold">{arrivalAirport}</BodyText>
        <Caption variant="smallalt" tabletVariant="medium" colour="light-foreground-alt">
          {intl.formatMessage(
            {
              id: 'zUGB3fA16aUpt99X26Qp',
              defaultMessage: '{airportName} ({airportCode})',
              description: 'Arrival airport name with code in flight journey detail modal',
            },
            { airportName: arrivalAirportName, airportCode: arrivalAirport },
          )}
        </Caption>
      </FlightItemLocation>
    </FlightItem>
  </Details>
}

function mapStateToProps(state: App.State) {
  return {
    isSpoofed: isSpoofed(state),
  }
}
export default connect(mapStateToProps)(JourneyV2DetailsModalFlight)
